import React, { useMemo, useRef } from 'react';
import classes from './Slider.module.scss';
import classNames from 'classnames';
import { round } from 'lodash';
import Slider from 'rc-slider';
import { useSlider } from '../../../../hooks/useSlider';

type MarksType = {
  [key: string]: JSX.Element;
};

export type SliderViewProps = {
  max: number;
  onChange: (value: number) => void;
  startingValue: number | undefined;
};

export const SliderView = ({ max = 0, onChange, startingValue }: SliderViewProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);

  const { maxValue, percentageValue, maxMarkerPercent, plusOneMarkerPercent, baseStepValue, onChangeHandler } =
    useSlider({
      maxValue: max,
      startingValue,
      ref
    });

  const sliderClasses = classNames(classes.Slider, { [classes.SliderZero]: maxValue === 0 });
  const headerLabelClasses = classNames('margin-bottom-small-32', classes.HeaderLabel);
  const maxMarkerClasses = classNames(classes.MaxMarker, {
    [classes.MaxMarkerActive]: percentageValue > maxMarkerPercent
  });
  const plusOneMarkerClasses = classNames(classes.PlusOneMarker, {
    [classes.PlusOneMarkerActive]: percentageValue > plusOneMarkerPercent
  });
  const plusTwoMarkerClasses = classNames(classes.PlusTwoMarker, {
    [classes.PlusTwoMarkerActive]: percentageValue === 100
  });

  const headerLabel = (): JSX.Element => {
    let calculatedValue = Number((percentageValue / baseStepValue).toFixed(2)); // Because we force placement of the maxValue marker, we need to adjust for the percentage not being correct
    calculatedValue = calculatedValue > maxValue - 1 ? maxValue : round(calculatedValue);

    const plusLabel =
      percentageValue >= plusOneMarkerPercent && percentageValue < 100
        ? '(+1)'
        : percentageValue === 100
        ? '(+2)'
        : null;

    return maxValue === 0 ? (
      <span>you have no future posts</span>
    ) : (
      <span>
        show future posts - {Math.min(calculatedValue, maxValue)} out of {maxValue} {plusLabel}
      </span>
    );
  };

  const markers = useMemo(() => {
    const marks: MarksType = {};

    for (let i = 1; i < maxValue; i++) {
      const position = (i * baseStepValue).toFixed(2);
      marks[position] = <div />;
    }

    marks[maxMarkerPercent.toString()] = <div className={maxMarkerClasses}>{max}</div>;
    marks[plusOneMarkerPercent.toString()] = <div className={plusOneMarkerClasses}>+1</div>;
    marks['100'] = <div className={plusTwoMarkerClasses}>+2</div>;

    return marks;
  }, [
    baseStepValue,
    max,
    maxMarkerClasses,
    maxMarkerPercent,
    maxValue,
    plusOneMarkerClasses,
    plusOneMarkerPercent,
    plusTwoMarkerClasses
  ]);

  return (
    <div ref={ref} className={sliderClasses} id={'ig-grid-slider-component'}>
      <div className={headerLabelClasses}>{headerLabel()}</div>
      <Slider
        min={0}
        marks={markers}
        step={null}
        onChange={(value: number | number[]) => {
          if (typeof value === 'number') {
            onChangeHandler(value, onChange);
          }
        }}
        value={maxValue === 0 ? maxMarkerPercent : percentageValue}
      />
    </div>
  );
};
